import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';

function ActiveUsers() {
  const { csrfToken, fetchCsrfToken } = useAuth();
  const navigate = useNavigate();
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const gridRef = useRef(null);

  const columnDefs = useMemo(() => [
    { field: 'business_name', headerName: 'Business Name', filter: 'agTextColumnFilter', sortable: true, flex: 2 },
    { field: 'email', headerName: 'Email', filter: 'agTextColumnFilter', sortable: true, flex: 2 },
    { field: 'tier', headerName: 'Tier', filter: 'agTextColumnFilter', sortable: true, flex: 1 },
    { 
      field: 'tot_images_avail', 
      headerName: 'Total Images Avail', 
      filter: 'agNumberColumnFilter', 
      sortable: true, 
      flex: 1,
      valueFormatter: (p) => p.value || 0 
    },
    { 
      field: 'image_returns', 
      headerName: 'Image Returns', 
      filter: 'agNumberColumnFilter', 
      sortable: true, 
      flex: 1,
      valueFormatter: (p) => p.value || 0 
    },
    { field: 'total_sales', headerName: 'Total Sales', filter: 'agNumberColumnFilter', sortable: true, flex: 1 },
    { field: 'total_images', headerName: 'Total Uploads', filter: 'agNumberColumnFilter', sortable: true, flex: 1 },
    { field: 'total_items', headerName: 'Total Items', filter: 'agNumberColumnFilter', sortable: true, flex: 1 },
    {
      field: 'end_date',
      headerName: 'End Date',
      valueFormatter: (params) => params.value ? DateTime.fromISO(params.value).toLocaleString(DateTime.DATE_MED) : 'N/A',
      filter: 'agDateColumnFilter',
      sortable: true,
      flex: 1,
      cellStyle: (params) => params.data.cancel_at ? { color: 'red' } : null,
    },
    {
      headerName: 'Actions',
      cellRenderer: (params) => (
        <div className="flex space-x-2">
          <button 
            onClick={() => handleViewDetails(params.data.client_id)} 
            className="text-blue-500 hover:underline text-sm"
          >
            Details
          </button>
        </div>
      ),
      flex: 1,
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    flex: 1,
    minWidth: 150,
    filter: true,
    resizable: true,
    sortable: true,
  }), []);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    setError(null);
    let currentCsrf = csrfToken;
    try {
      if (!currentCsrf) {
        currentCsrf = await fetchCsrfToken();
      }
      const res = await fetch(`${process.env.REACT_APP_API_URL}/supabase/get-client-overviews`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'x-csrf-token': currentCsrf 
        },
        body: JSON.stringify({}),
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Fetch clients failed: ${res.statusText}`);
      const data = await res.json();
      const sanitizedData = data.map(row => ({
        client_id: row.client_id,
        business_name: row.business_name || 'Unknown',
        email: row.email || 'N/A',
        tier: row.tier || 'None',
        tot_images_avail: row.tot_images_avail || 0,
        image_returns: row.image_returns || 0,
        total_sales: row.total_sales || 0,
        total_images: row.total_images || 0,
        total_items: row.total_items || 0,
        end_date: row.end_date,
        cancel_at: row.cancel_at,
      }));
      setRowData(sanitizedData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getRowStyle = (params) => {
    if (params.data.cancel_at) {
      return { backgroundColor: 'rgba(255, 255, 0, 0.2)' }; // Highlight row if cancelling
    }
    return null;
  };

  const handleViewDetails = (clientId) => {
    navigate(`/admin/client-details/${clientId}`);
  };

  const handleBackToAdmin = () => {
    navigate('/admin');
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100 p-4">
      <div className="flex justify-between mb-4 items-center">
        <h2 className="text-2xl font-bold text-gray-800">Active Users - Admin Overview</h2>
        <button 
          onClick={handleBackToAdmin} 
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
        >
          Back to Admin
        </button>
      </div>
      {loading && <p className="text-gray-500 text-center py-4">Loading...</p>}
      {error && <p className="text-red-500 mb-4 text-center bg-red-50 p-2 rounded">{error}</p>}
      <div style={{ height: 600, width: '100%' }}>
        <AgGridReact
          className="ag-theme-quartz"
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={25}
          paginationPageSizeSelector={[25, 50, 100]}
          getRowStyle={getRowStyle}
          animateRows={true}
        />
      </div>
    </div>
  );
}

export default ActiveUsers;